import { useAppTranslation } from '../i18n/useAppTranslation';
import { useState } from 'react';
import { Box, Typography, Menu, MenuItem, ListItemIcon, useMediaQuery, useTheme } from '@mui/material';
import { Science, PersonAdd, Logout, ExpandMore } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

/**
 * Badge compact « Mode démo » affiche lorsqu'un visiteur explore un compte de
 * demonstration. Centre en haut de l'ecran (sous l'en-tete sur mobile), il est
 * cliquable pour ouvrir les actions : creer un compte ou quitter la demo.
 *
 * Remplace l'ancien bandeau orange pleine largeur, juge trop intrusif.
 */
const DemoBanner = () => {
  const { tr } = useAppTranslation();
  const { isDemo, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);

  if (!isDemo) return null;

  return (
    <>
      <Box
        onClick={(e) => setAnchor(e.currentTarget)}
        role="button"
        tabIndex={0}
        aria-label={tr("Mode démo — options")}
        sx={{
          position: 'fixed',
          // Sous l'en-tete fixe sur mobile ; flotte en haut sur grand ecran.
          top: isMobile ? 'calc(72px + env(safe-area-inset-top))' : 'calc(12px + env(safe-area-inset-top))',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: (t) => t.zIndex.appBar + 2,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.6,
          px: 1.6,
          py: 0.5,
          borderRadius: 999,
          cursor: 'pointer',
          color: '#1F2937',
          background: 'linear-gradient(90deg, #FBBF24 0%, #F59E0B 100%)',
          boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
          userSelect: 'none',
          transition: 'filter .15s ease, transform .15s ease',
          '&:hover': { filter: 'brightness(1.05)' },
          '&:active': { transform: 'translateX(-50%) scale(0.97)' },
        }}
      >
        <Science sx={{ fontSize: 17 }} />
        <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: 0.3, whiteSpace: 'nowrap' }}>
           {tr("Mode démo")} </Typography>
        <ExpandMore sx={{ fontSize: 17 }} />
      </Box>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MenuItem onClick={() => { setAnchor(null); logout('/register'); }}>
          <ListItemIcon><PersonAdd fontSize="small" /></ListItemIcon>
           {tr("Créer un compte")} </MenuItem>
        <MenuItem onClick={() => { setAnchor(null); logout('/'); }}>
          <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
           {tr("Quitter la démo")} </MenuItem>
      </Menu>
    </>
  );
};

export default DemoBanner;
